import { useDeferredValue, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { useThree } from '@react-three/fiber';
import { PerspectiveCamera } from 'three';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

import { useAppDispatch, useAppSelector } from '../../../hooks.ts';
import { useStore } from '../../../pages/workspace/hooks.ts';
import {
  MeasurementData,
  setCameraInitialized,
  setUseOriginalTextures,
} from '../../../store/reducers/workspace.reducer.ts';
import { getNorms } from '../../../store/selectors/user.selector';
import { RootState } from '../../../store/store.ts';
import { MEASUREMENT_VISUALIZATION_FIELD_MAP, MeasurementVisualizationType } from '../../../types/view';
import { centerCameraOnModel } from '../../../utils/cameraUtils';
import { cloneParsedModel, loadParsedModel } from '../../../utils/modelLoadCache';
import { requirementFromMesh } from '../../../utils/photometricTargets';
import { hasOriginalTextures } from '../../../utils/threeUtils';
import { heatmapWorkerManager } from '../../../workers/heatmapWorkerManager';
import {
  FIELD_INDEX_MAP,
  calculateDisplayScale,
  decompressMeasurementData,
  getThreeColorAtPosition,
} from './measurementUtils';

type ModelType = {
  name: string;
  url: string;
};

interface MeasurementPoint {
  position: THREE.Vector3;
  color: THREE.Color;
}

/**
 * Geometric context derived from the *unfiltered* point cloud. We use this so
 * the heatmap's spatial scale (texture bbox, gaussian influence radius) stays
 * constant regardless of how many points pass the legend filter. Otherwise a
 * filter that lets through only a few far-apart points produces a "natural
 * neighbor distance" of huge magnitude, which blows the influence radius up
 * and paints the entire model with the surviving points' colors.
 */
interface DataContext {
  bbox: { minX: number; maxX: number; minY: number; maxY: number };
  /** Approx. spacing between adjacent samples on a uniform grid. */
  naturalSpacing: number;
}

interface BuildResult {
  points: MeasurementPoint[];
  context: DataContext | null;
}

const transformMatrix = new THREE.Matrix4();
transformMatrix.set(1, 0, 0, 0, 0, 0, -1, 0, 0, 1, 0, 0, 0, 0, 0, 1);

function buildMeasurementPoints(
  data: MeasurementData[],
  visualizationType: MeasurementVisualizationType,
  filterRange: { min: number | null; max: number | null } | null,
): BuildResult {
  const allPoints: MeasurementPoint[] = [];
  if (!data || data.length === 0) return { points: allPoints, context: null };

  const fieldKey = MEASUREMENT_VISUALIZATION_FIELD_MAP[visualizationType];

  const allLayerData: { name: string; positions: number[][]; measurements: number[][] }[] = [];
  for (const layer of data) {
    if (!layer.positions || !layer.measurements) continue;
    const positions = decompressMeasurementData<number[][]>(layer.positions);
    const measurements = decompressMeasurementData<number[][]>(layer.measurements);
    allLayerData.push({ name: layer.name, positions, measurements });
  }

  // Compute bbox + spacing from ALL points up front. Crucially these are
  // filter-independent so the heatmap shape stays stable as the user drags.
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  let totalCount = 0;
  for (const layer of allLayerData) {
    for (const pos of layer.positions) {
      if (pos[0] < minX) minX = pos[0];
      if (pos[0] > maxX) maxX = pos[0];
      if (pos[1] < minY) minY = pos[1];
      if (pos[1] > maxY) maxY = pos[1];
      totalCount++;
    }
  }
  const rangeX = maxX - minX;
  const rangeY = maxY - minY;
  // Estimate spacing assuming a roughly uniform sampling grid. sqrt(area / N)
  // gives a sensible "expected distance to nearest neighbor" without needing
  // an O(N^2) nearest-neighbor pass.
  const area = Math.max(rangeX * rangeY, 1e-6);
  const naturalSpacing = totalCount > 0 ? Math.sqrt(area / totalCount) : 0;
  const context: DataContext | null =
    totalCount > 0 && rangeX > 0 && rangeY > 0 && naturalSpacing > 0
      ? { bbox: { minX, maxX, minY, maxY }, naturalSpacing }
      : null;

  // Pull the shared display scale: norm-anchored when a target is declared on
  // any layer (max = NORM_SCALE_MULTIPLIER x target), otherwise fall back to
  // actual data range.
  const scale = visualizationType !== 'tamanScore' ? calculateDisplayScale(data, visualizationType) : null;
  const globalMin = scale ? scale.min : 0;
  const globalMax = scale ? scale.max : 1;

  // Filter only applies to value-based modes.
  const applyFilter = filterRange !== null && visualizationType !== 'tamanScore' && fieldKey !== null;

  const expectedMap = new Map<
    string,
    { L: number | null; E: number | null; Ev: number | null; Esc: number | null; Ehs: number | null }
  >();
  if (visualizationType === 'tamanScore') {
    for (const layer of data) {
      const getExpected = (fn: string) => {
        const ev = layer.expectedValues?.find((v) => v.function === fn);
        return ev ? ev.value : null;
      };
      expectedMap.set(layer.name, {
        L: getExpected('L'),
        E: getExpected('E'),
        Ev: getExpected('Ev'),
        Esc: getExpected('Esc'),
        Ehs: getExpected('Ehs'),
      });
    }
  }

  for (const { name, positions, measurements } of allLayerData) {
    const expected = expectedMap.get(name);

    for (let i = 0; i < positions.length; i++) {
      const pos = positions[i];
      const meas = measurements[i];

      if (applyFilter && fieldKey) {
        const v = meas[FIELD_INDEX_MAP[fieldKey]];
        // Out-of-range / missing values don't contribute to the heatmap so the
        // colored area visually shrinks to just the filtered region. Each edge
        // is independently nullable — `null` means "no constraint on this side"
        // so values past that edge pass through unfiltered.
        if (v === -1.0) continue;
        if (filterRange.min !== null && v < filterRange.min) continue;
        if (filterRange.max !== null && v > filterRange.max) continue;
      }

      const position = new THREE.Vector3(pos[0], pos[1], pos[2]);

      let color: THREE.Color;

      if (visualizationType === 'tamanScore') {
        color = computeTamanScoreColor(meas, expected ?? null);
      } else if (fieldKey) {
        const value = meas[FIELD_INDEX_MAP[fieldKey]];
        if (value === -1.0 || globalMax === globalMin) {
          color = new THREE.Color(0x404040);
        } else {
          const normalized = (value - globalMin) / (globalMax - globalMin);
          color = getThreeColorAtPosition(normalized);
        }
      } else {
        color = new THREE.Color(0x404040);
      }

      allPoints.push({ position, color });
    }
  }

  return { points: allPoints, context };
}

function computeTamanScoreColor(
  meas: number[],
  expected: { L: number | null; E: number | null; Ev: number | null; Esc: number | null; Ehs: number | null } | null,
): THREE.Color {
  if (!expected) return new THREE.Color(0x404040);

  const fields: [number, number | null][] = [
    [meas[FIELD_INDEX_MAP.L], expected.L],
    [meas[FIELD_INDEX_MAP.E], expected.E],
    [meas[FIELD_INDEX_MAP.Ev], expected.Ev],
    [meas[FIELD_INDEX_MAP.Esc], expected.Esc],
    [meas[FIELD_INDEX_MAP.Ehs], expected.Ehs],
  ];

  for (const [actual, exp] of fields) {
    if (actual !== -1.0 && exp !== null && exp > 0) {
      const pctDiff = Math.abs((actual - exp) / exp);
      const green = new THREE.Color(0x00ff00);
      const red = new THREE.Color(0xff0000);
      return new THREE.Color().lerpColors(green, red, Math.min(pctDiff, 1));
    }
  }

  return new THREE.Color(0x404040);
}

interface HeatmapTexture {
  texture: THREE.DataTexture;
  boundsMin: THREE.Vector2;
  boundsMax: THREE.Vector2;
}

/**
 * Bake IDW-interpolated colors into a 2D texture on the CPU.
 * Uses the same 2D projection as the shader: (worldX, -worldZ).
 * Measurement points are first projected into this space before baking.
 */
/** Separable Gaussian blur on RGBA Uint8Array (in-place). radius is in texels. */
function blurRGBA(data: Uint8Array, w: number, h: number, radius: number): void {
  const kernel: number[] = [];
  const sigma = radius / 2;
  let sum = 0;
  for (let i = -radius; i <= radius; i++) {
    const v = Math.exp(-(i * i) / (2 * sigma * sigma));
    kernel.push(v);
    sum += v;
  }
  for (let i = 0; i < kernel.length; i++) kernel[i] /= sum;

  const tmp = new Uint8Array(data.length);

  // Horizontal pass
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let r = 0,
        g = 0,
        b = 0,
        a = 0;
      for (let k = -radius; k <= radius; k++) {
        const sx = Math.min(w - 1, Math.max(0, x + k));
        const si = (y * w + sx) * 4;
        const wt = kernel[k + radius];
        r += data[si] * wt;
        g += data[si + 1] * wt;
        b += data[si + 2] * wt;
        a += data[si + 3] * wt;
      }
      const di = (y * w + x) * 4;
      tmp[di] = r;
      tmp[di + 1] = g;
      tmp[di + 2] = b;
      tmp[di + 3] = a;
    }
  }

  // Vertical pass
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let r = 0,
        g = 0,
        b = 0,
        a = 0;
      for (let k = -radius; k <= radius; k++) {
        const sy = Math.min(h - 1, Math.max(0, y + k));
        const si = (sy * w + x) * 4;
        const wt = kernel[k + radius];
        r += tmp[si] * wt;
        g += tmp[si + 1] * wt;
        b += tmp[si + 2] * wt;
        a += tmp[si + 3] * wt;
      }
      const di = (y * w + x) * 4;
      data[di] = r;
      data[di + 1] = g;
      data[di + 2] = b;
      data[di + 3] = a;
    }
  }
}

/**
 * Pack the freshly built measurement points into the typed-array shape the
 * heatmap worker expects. We only need (x, y) and (r, g, b) — the worker
 * speaks plain numbers and constructs nothing three-shaped.
 */
function packPointsForWorker(points: MeasurementPoint[]): { positions: Float32Array; colors: Float32Array } {
  const n = points.length;
  const positions = new Float32Array(n * 2);
  const colors = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    const p = points[i];
    positions[i * 2] = p.position.x;
    positions[i * 2 + 1] = p.position.y;
    colors[i * 3] = p.color.r;
    colors[i * 3 + 1] = p.color.g;
    colors[i * 3 + 2] = p.color.b;
  }
  return { positions, colors };
}

/**
 * Wrap a baked RGBA byte buffer (from the worker, or the main-thread
 * fallback below) into the `HeatmapTexture` shape the shader material
 * expects. Filtering / wrap modes match what the original synchronous
 * baker produced so the visual output is identical.
 */
function buildHeatmapTexture(
  data: Uint8Array,
  resX: number,
  resY: number,
  boundsMin: [number, number],
  boundsMax: [number, number],
): HeatmapTexture {
  const texture = new THREE.DataTexture(data, resX, resY, THREE.RGBAFormat, THREE.UnsignedByteType);
  texture.flipY = false;
  texture.needsUpdate = true;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  return {
    texture,
    boundsMin: new THREE.Vector2(boundsMin[0], boundsMin[1]),
    boundsMax: new THREE.Vector2(boundsMax[0], boundsMax[1]),
  };
}

/**
 * Main-thread fallback baker. Only runs when the worker fails to initialise
 * (e.g. a browser that blocks module workers). Kept in lockstep with the
 * worker's bake() so visuals stay identical across the two paths.
 */
function bakeHeatmapTextureOnMainThread(
  points: MeasurementPoint[],
  context: DataContext,
  resolution: number = 512,
): HeatmapTexture {
  // Project measurement points to the 2D ground-plane axes the shader uses.
  const projected = points.map((pt) => ({
    u: pt.position.x,
    v: pt.position.y,
    color: pt.color,
  }));

  // Use the *unfiltered* data bbox so the texture footprint stays constant
  // as the user drags the legend filter. The shader discards fragments
  // outside this bbox, keeping the colored region anchored to the actual
  // measured area rather than the (possibly tiny) filtered subset's bbox.
  const { minX: rawMinX, maxX: rawMaxX, minY: rawMinY, maxY: rawMaxY } = context.bbox;
  const padding = Math.max(rawMaxX - rawMinX, rawMaxY - rawMinY) * 0.05;
  const minX = rawMinX - padding;
  const maxX = rawMaxX + padding;
  const minY = rawMinY - padding;
  const maxY = rawMaxY + padding;
  const rangeX = maxX - minX;
  const rangeY = maxY - minY;

  // Make resolution proportional to aspect ratio
  const aspect = rangeX / rangeY;
  const resX = aspect >= 1 ? resolution : Math.max(64, Math.round(resolution * aspect));
  const resY = aspect >= 1 ? Math.max(64, Math.round(resolution / aspect)) : resolution;

  // Tie the influence radius to the *natural* sample spacing (derived from
  // all points, not just the filtered survivors). With few filtered points
  // each one only colors its local neighborhood — no runaway spread.
  // ~4x spacing covers the gap between adjacent samples without bleeding
  // far beyond.
  const maxDist = context.naturalSpacing * 4;

  // Gaussian sigma: controls how quickly influence fades.
  // Set so that at maxDist the weight is near zero.
  const sigma = maxDist / 3;
  const data = new Uint8Array(resX * resY * 4);
  const baseColor = new THREE.Color(0x404040);

  const cellSize = maxDist;
  // Bias each axis so encoded coords stay non-negative, then pack along a
  // stride > 2*GRID_BIAS to keep rows from colliding across the sign boundary.
  const GRID_BIAS = 1 << 20;
  const GRID_STRIDE = 1 << 21;
  const encodeKey = (cx: number, cy: number) => (cx + GRID_BIAS) * GRID_STRIDE + (cy + GRID_BIAS);
  const gridMap = new Map<number, number[]>();
  for (let i = 0; i < projected.length; i++) {
    const cx = Math.floor(projected[i].u / cellSize);
    const cy = Math.floor(projected[i].v / cellSize);
    const key = encodeKey(cx, cy);
    let list = gridMap.get(key);
    if (!list) {
      list = [];
      gridMap.set(key, list);
    }
    list.push(i);
  }

  // Hoist invariant out of the inner loop.
  const twoSigmaSq = 2 * sigma * sigma;
  const maxDistSq = maxDist * maxDist;

  for (let py = 0; py < resY; py++) {
    for (let px = 0; px < resX; px++) {
      const texU = minX + ((px + 0.5) / resX) * rangeX;
      const texV = minY + ((py + 0.5) / resY) * rangeY;

      let weightSum = 0;
      let maxWeight = 0;
      let r = 0,
        g = 0,
        b = 0;

      const gcx = Math.floor(texU / cellSize);
      const gcy = Math.floor(texV / cellSize);

      for (let dx = -1; dx <= 1; dx++) {
        for (let dy = -1; dy <= 1; dy++) {
          const indices = gridMap.get(encodeKey(gcx + dx, gcy + dy));
          if (!indices) continue;

          for (const i of indices) {
            const p = projected[i];
            const ddx = p.u - texU;
            const ddy = p.v - texV;
            const distSq = ddx * ddx + ddy * ddy;

            if (distSq > maxDistSq) continue;

            const w = Math.exp(-distSq / twoSigmaSq);
            if (w > maxWeight) maxWeight = w;
            weightSum += w;
            r += w * p.color.r;
            g += w * p.color.g;
            b += w * p.color.b;
          }
        }
      }

      const idx = (py * resX + px) * 4;
      if (weightSum > 0) {
        const fr = r / weightSum;
        const fg = g / weightSum;
        const fb = b / weightSum;
        data[idx + 0] = Math.round(Math.min(1, fr) * 255);
        data[idx + 1] = Math.round(Math.min(1, fg) * 255);
        data[idx + 2] = Math.round(Math.min(1, fb) * 255);
        // Alpha driven by peak Gaussian weight — smooth fade at edges
        data[idx + 3] = Math.round(Math.min(1, maxWeight) * 255);
      } else {
        data[idx + 0] = Math.round(baseColor.r * 255);
        data[idx + 1] = Math.round(baseColor.g * 255);
        data[idx + 2] = Math.round(baseColor.b * 255);
        data[idx + 3] = 0; // no measurement coverage
      }
    }
  }

  // Separable Gaussian blur to eliminate texel grid pattern
  blurRGBA(data, resX, resY, 2);

  return buildHeatmapTexture(data, resX, resY, [minX, minY], [maxX, maxY]);
}

const HEATMAP_VERTEX_SHADER = /* glsl */ `
varying vec3 vWorldPosition;
varying vec3 vWorldNormal;

void main() {
  vec4 worldPos = modelMatrix * vec4(position, 1.0);
  vWorldPosition = worldPos.xyz;
  vWorldNormal = normalize((modelMatrix * vec4(normal, 0.0)).xyz);
  gl_Position = projectionMatrix * viewMatrix * worldPos;
}
`;

// The heatmap is baked using measurement point (x, y) as the 2D axes.
// Measurement point spheres render in world space at (pt.x, pt.y, pt.z),
// so the shader should look up using (worldX, worldY).
const HEATMAP_FRAGMENT_SHADER = /* glsl */ `
uniform sampler2D heatmap;
uniform vec2 boundsMin;
uniform vec2 boundsMax;

varying vec3 vWorldPosition;
varying vec3 vWorldNormal;

void main() {
  vec3 norm = normalize(vWorldNormal);

  vec2 uvRaw = vec2(
    (vWorldPosition.x - boundsMin.x) / (boundsMax.x - boundsMin.x),
    (vWorldPosition.y - boundsMin.y) / (boundsMax.y - boundsMin.y)
  );
  // Anything outside the actual measured area should fall back to the
  // neutral base color — without this, sampling clamps to the texture edge,
  // which can leak the surviving filter points' colors across the whole model.
  bool insideBounds = uvRaw.x >= 0.0 && uvRaw.x <= 1.0 && uvRaw.y >= 0.0 && uvRaw.y <= 1.0;
  vec2 uv = clamp(uvRaw, 0.0, 1.0);

  vec4 heatmapColor = texture2D(heatmap, uv);

  vec3 baseColor = vec3(0.25, 0.25, 0.25);
  float coverage = insideBounds ? heatmapColor.a : 0.0;

  // Prevent color leaking to the underside of surfaces.
  // Measurement points sit on upward/outward-facing surfaces.
  // Use the vertical component of the normal (Z in scene space)
  // to suppress color on downward-facing fragments.
  // norm.z > 0 = facing up (show color), norm.z < 0 = facing down (gray)
  // Smooth transition for angled surfaces.
  float normalGate = smoothstep(-0.1, 0.3, norm.z);
  coverage *= normalGate;

  vec3 finalColor = mix(baseColor, heatmapColor.rgb, coverage);

  float ambient = 0.4;
  float diffuse = max(dot(norm, normalize(vec3(1.0, 2.0, 1.0))), 0.0) * 0.6;
  gl_FragColor = vec4(finalColor * (ambient + diffuse), 1.0);
}
`;

function createHeatmapMaterial(heatmap: HeatmapTexture): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: {
      heatmap: { value: heatmap.texture },
      boundsMin: { value: heatmap.boundsMin },
      boundsMax: { value: heatmap.boundsMax },
    },
    vertexShader: HEATMAP_VERTEX_SHADER,
    fragmentShader: HEATMAP_FRAGMENT_SHADER,
    side: THREE.DoubleSide,
  });
}

interface MeasurementColoredModelProps {
  model: ModelType;
  measurementData: MeasurementData[];
  visualizationType: MeasurementVisualizationType;
  onLoad?: () => void;
}

export const MeasurementColoredModel = ({
  model,
  measurementData,
  visualizationType,
  onLoad,
}: MeasurementColoredModelProps) => {
  const {
    selectedLayer,
    hoveredLayer,
    selectedObject,
    setSelectedObject,
    setHoveredLayer,
    project,
    activeVersion,
    setModelLayers,
    setSelectedLayer,
    handleLayerClick,
  } = useStore();
  const norms = useSelector(getNorms);
  const dispatch = useAppDispatch();
  const filterRangeImmediate = useAppSelector((state) => state.workspace.measurementFilterRange);
  // Defer the heatmap-driving filter so React can drop intermediate values
  // while the user is dragging the legend handles. The cheap UI (legend +
  // 3D points) still uses the immediate value; only the expensive IDW bake
  // is batched, which keeps drags buttery while still updating live.
  const filterRange = useDeferredValue(filterRangeImmediate);
  const [loadedObject, setLoadedObject] = useState<THREE.Object3D | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const { camera, controls } = useThree((state) => ({
    camera: state.camera as PerspectiveCamera,
    controls: state.controls as OrbitControls,
  }));

  const buildResult = useMemo(
    () => buildMeasurementPoints(measurementData, visualizationType, filterRange),
    [measurementData, visualizationType, filterRange],
  );

  // Heatmap baking is the dominant CPU cost when entering measurement view.
  // We push it onto a Web Worker so the main thread (and therefore React +
  // input handlers + the canvas) stays responsive during the multi-second
  // IDW + Gaussian-blur pass. The model renders with the neutral fallback
  // material below until the worker reports back, then the shader material
  // is applied via the selection/hover effect lower in this file.
  const [heatmap, setHeatmap] = useState<HeatmapTexture | null>(null);

  useEffect(() => {
    if (buildResult.points.length === 0 || !buildResult.context) {
      setHeatmap(null);
      return;
    }

    // Fallback for environments where the worker failed to initialise.
    // Worse UX (UI freezes), but preserves correctness.
    if (!heatmapWorkerManager.isAvailable()) {
      setHeatmap(bakeHeatmapTextureOnMainThread(buildResult.points, buildResult.context, 512));
      return;
    }

    let cancelled = false;
    const { positions, colors } = packPointsForWorker(buildResult.points);
    const { promise, cancel } = heatmapWorkerManager.bake({
      positions,
      colors,
      bbox: buildResult.context.bbox,
      naturalSpacing: buildResult.context.naturalSpacing,
      resolution: 512,
    });

    promise
      .then((result) => {
        if (cancelled) return;
        setHeatmap(buildHeatmapTexture(result.data, result.resX, result.resY, result.boundsMin, result.boundsMax));
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Heatmap bake failed:', err);
        setHeatmap(null);
      });

    return () => {
      cancelled = true;
      cancel();
    };
  }, [buildResult]);

  // Dispose the GPU texture when a new bake replaces it (or on unmount) so
  // repeated filter drags don't leak DataTextures in WebGL memory.
  useEffect(() => {
    if (!heatmap) return;
    return () => {
      heatmap.texture.dispose();
    };
  }, [heatmap]);

  useEffect(() => {
    if (hasLoaded && loadedObject) return;
    const loadModel = async () => {
      let extension = model.name.split('.').pop()?.toLowerCase();
      if (!extension || extension === model.name.toLowerCase()) {
        const urlParts = model.url.split('?')[0].split('.');
        if (urlParts.length > 1) {
          extension = urlParts.pop()?.toLowerCase();
        }
      }
      if (extension !== 'glb' && extension !== 'gltf' && extension !== 'obj') {
        console.error(`Unsupported file format: ${extension} for file ${model.name}`);
        return;
      }
      try {
        // Reuse the parse from the editing view (shared cache); clone for our own
        // material/transform edits so the first switch into measurement mode no
        // longer re-parses the whole model.
        const { object, originalNames: originalNamesMap } = cloneParsedModel(
          await loadParsedModel(model.url, extension),
        );

        const layers: {
          uuid: string;
          groupId?: string | null;
          threeJsName: string;
          originalName: string;
          displayName: string;
          color?: string;
          modelName: string;
          visible: boolean;
          isIlluminated: boolean;
          canHoldLuminaire: boolean;
          layerType: string;
          selectedNorm: null | string;
          customTargets?: ReturnType<typeof requirementFromMesh>['customTargets'];
        }[] = [];

        const meshes = activeVersion?.meshes || [];
        const meshObjectsMap = new Map<string, THREE.Mesh>();
        let counter = 0;
        object.traverse((child: THREE.Object3D) => {
          if (child instanceof THREE.Mesh) {
            if (extension === 'glb' || extension === 'gltf') {
              child.applyMatrix4(transformMatrix);
            }
            const threeJsName = child.name || `Mesh_${counter}`;
            const originalName = originalNamesMap.get(counter);
            if (originalName) meshObjectsMap.set(originalName, child);
            child.userData.originalMaterial = child.material.clone();
            child.userData.layerName = threeJsName;
            child.userData.originalName = originalName || threeJsName;
            child.userData.interactive = true;
            child.userData.onClick = () => {
              handleLayerClick(threeJsName, originalName || threeJsName);
            };
            counter++;
          }
        });
        meshes.forEach((backendMesh) => {
          const meshObject = meshObjectsMap.get(backendMesh.name);
          let layerType = 'Unspecified';
          if (backendMesh.type) {
            layerType = backendMesh.type.charAt(0).toUpperCase() + backendMesh.type.slice(1).toLowerCase();
          }
          const requirement = requirementFromMesh(backendMesh, norms);
          const threeJsName = meshObject ? meshObject.name || backendMesh.name : backendMesh.name;
          layers.push({
            uuid: backendMesh.uuid,
            groupId: backendMesh.groupId,
            threeJsName: threeJsName,
            originalName: backendMesh.name,
            displayName: backendMesh.displayName || backendMesh.name,
            color: backendMesh.color,
            modelName: model.name,
            visible: true,
            isIlluminated: requirement.isIlluminated,
            canHoldLuminaire: backendMesh.canHoldLight,
            layerType: layerType,
            selectedNorm: requirement.selectedNorm,
            customTargets: requirement.customTargets,
          });
          if (meshObject) {
            meshObject.userData.layerName = threeJsName;
            meshObject.userData.onClick = () => {
              handleLayerClick(threeJsName, backendMesh.name);
            };
          }
        });
        setModelLayers(layers);
        setLoadedObject(object);

        const modelHasTextures = hasOriginalTextures(object);
        dispatch(setUseOriginalTextures(modelHasTextures));

        setIsLoading(false);
        setHasLoaded(true);
        if (onLoad) onLoad();
      } catch (error) {
        console.error('Error loading model:', error);
        setIsLoading(false);
      }
    };
    loadModel();
  }, [
    model,
    setModelLayers,
    setSelectedLayer,
    setSelectedObject,
    selectedLayer,
    setHoveredLayer,
    hasLoaded,
    loadedObject,
    project?.versions,
    project?.activeVersionId,
    activeVersion?.meshes,
    norms,
    handleLayerClick,
    onLoad,
    dispatch,
  ]);

  const cameraInitialized = useSelector((state: RootState) => state.workspace.cameraInitialized);

  useEffect(() => {
    if (!loadedObject) return;
    if (!cameraInitialized) {
      centerCameraOnModel(loadedObject, camera, controls);
      dispatch(setCameraInitialized(true));
    }
  }, [loadedObject, camera, controls, cameraInitialized, dispatch]);

  // Apply heatmap shader material
  useEffect(() => {
    if (!loadedObject) return;

    loadedObject.traverse((child: THREE.Object3D) => {
      if (!(child instanceof THREE.Mesh)) return;

      const isSelected = selectedObject === child.uuid || selectedLayer === child.userData.layerName;
      // Hover accent is suppressed in measurement view — the heatmap colouring
      // is the primary visual and the green highlight would obscure it.
      const isHovered = false;

      if (isSelected) {
        child.material = new THREE.MeshStandardMaterial({
          color: '#ff9136',
          transparent: true,
          opacity: 0.8,
          emissive: '#ff9136',
          emissiveIntensity: 0.5,
          side: THREE.DoubleSide,
        });
        return;
      }

      if (isHovered) {
        child.material = new THREE.MeshStandardMaterial({
          color: '#B3EC49',
          transparent: true,
          opacity: 0.8,
          emissive: '#B3EC49',
          emissiveIntensity: 0.5,
          side: THREE.DoubleSide,
        });
        return;
      }

      if (heatmap) {
        child.material = createHeatmapMaterial(heatmap);
      } else {
        // No measurement points contribute (e.g. the legend filter excludes
        // everything). Fall back to the neutral "no coverage" tone the heatmap
        // shader would have drawn for empty areas, so the surface visibly
        // reflects the empty filter instead of showing a stale prior bake.
        child.material = new THREE.MeshStandardMaterial({
          color: '#404040',
          side: THREE.DoubleSide,
        });
      }
    });
  }, [selectedLayer, hoveredLayer, selectedObject, loadedObject, heatmap]);

  if (isLoading) {
    return (
      <mesh position={[0, 0, 0]}>
        <boxGeometry args={[1, 1, 1]} />
        <meshStandardMaterial color="#B3EC49" wireframe={true} />
      </mesh>
    );
  }

  if (!loadedObject) return null;

  return <primitive object={loadedObject} position={[0, 0, 0]} />;
};
